import React from 'react';
import {
  Box,
  Paper,
  CircularProgress,
  Alert,
  Button,
  Fab,
  Snackbar,
  Skeleton,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { SPACING } from '../../theme';
import {
  FiltersBar,
  PaginationBar,
  DeleteConfirmDialog,
  ExecutionHistoryDialog,
  TemplateAnalyticsDialog,
  TemplateExperimentsDialog,
  TemplateFeedbackDialog,
} from '../TemplateManagerDashboard/components';
import { TwoStepExperimentDialog } from '../ExperimentManager';
import {
  MESSAGE_DURATION_SNACKBAR,
  SEARCH_MIN_CHARS,
} from '../../config/constants';
import { useTemplateDashboard } from './hooks/useTemplateDashboard';
import {
  TemplateDashboardHeader,
  TemplatesGrid,
  EmptyState,
} from './components';

const TemplateDashboard = () => {
  const navigate = useNavigate();

  const {
    // State
    templates,
    loading,
    error,
    initialLoading,
    refreshing,
    totalPages,
    totalCount,
    currentPage,
    pageSize,
    searchQuery,
    categoryFilter,
    statusFilter,
    sortBy,
    isDebouncing,
    successVisible,
    setSuccessVisible,
    // Dialog state
    deleteDialogOpen,
    setDeleteDialogOpen,
    templateToDelete,
    setTemplateToDelete,
    deleteLoading,
    executionHistoryOpen,
    setExecutionHistoryOpen,
    selectedTemplate,
    experimentDialogOpen,
    setExperimentDialogOpen,
    templateForExperiment,
    setTemplateForExperiment,
    analyticsDialogOpen,
    setAnalyticsDialogOpen,
    selectedTemplateForAnalytics,
    setSelectedTemplateForAnalytics,
    feedbackDialogOpen,
    setFeedbackDialogOpen,
    selectedFeedbackTemplate,
    setSelectedFeedbackTemplate,
    experimentsDialogOpen,
    setExperimentsDialogOpen,
    selectedTemplateForExperiments,
    setSelectedTemplateForExperiments,
    executions,
    executionsLoading,
    snackbar,
    closeSnackbar,
    // Handlers
    refetch,
    handleRefresh,
    handleDeleteTemplate,
    handleEdit,
    handleCreateTemplate,
    handleViewExperiments,
    handleExperimentCreated,
    handleExecute,
    handleOpenVersions,
    handleViewAnalytics,
    handleOpenFeedback,
    openDeleteDialog,
    openExecutionHistory,
    goToPage,
    changePageSize,
    // Setters
    setSearchQuery,
    setCategoryFilter,
    setStatusFilter,
    setSortBy,
  } = useTemplateDashboard();

  if (initialLoading && !refreshing) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ p: SPACING.md }}>
        <TemplateDashboardHeader onCreateTemplate={handleCreateTemplate} />

        {/* Search and Filters */}
        <Paper
          sx={{
            px: SPACING.md,
            pt: SPACING.xxl,
            pb: SPACING.xxl,
            mb: SPACING.xxl,
          }}
        >
          <FiltersBar
            loading={loading}
            searchTerm={searchQuery}
            onSearchChange={setSearchQuery}
            category={categoryFilter}
            onCategoryChange={setCategoryFilter}
            status={statusFilter}
            onStatusChange={setStatusFilter}
            sortBy={sortBy}
            onSortByChange={setSortBy}
            onRefresh={handleRefresh}
            isDebouncing={isDebouncing}
            minSearchChars={SEARCH_MIN_CHARS}
            refreshing={refreshing}
            hideStatusFilter={false}
          />
        </Paper>

        {/* Error Display with Retry */}
        {error && (
          <Alert
            severity="error"
            sx={{ mb: 3 }}
            action={
              <Button color="inherit" size="small" onClick={refetch}>
                Retry
              </Button>
            }
          >
            {error}
          </Alert>
        )}

        {/* Success Message */}
        {templates.length > 0 && !loading && successVisible && (
          <Alert
            severity="success"
            sx={{ mb: SPACING.md }}
            onClose={() => setSuccessVisible(false)}
          >
            Loaded {templates.length} template
            {templates.length !== 1 ? 's' : ''} successfully
            {totalPages > 1 &&
              ` (Page ${currentPage} of ${totalPages}, ${totalCount} total)`}
          </Alert>
        )}

        {/* Templates Grid */}
        {refreshing ? (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
              gap: 3,
              alignItems: 'stretch',
            }}
          >
            {Array.from({ length: 6 }).map((_, idx) => (
              <Paper key={idx} sx={{ p: 2 }}>
                <Skeleton variant="text" width="60%" height={28} />
                <Skeleton variant="text" width="90%" />
                <Skeleton variant="rectangular" height={80} sx={{ my: 1 }} />
                <Skeleton variant="text" width="40%" />
              </Paper>
            ))}
          </Box>
        ) : (
          <TemplatesGrid
            templates={templates}
            onEdit={handleEdit}
            onExecute={handleExecute}
            onOpenVersions={handleOpenVersions}
            onOpenExecutions={openExecutionHistory}
            onDelete={openDeleteDialog}
            onCreateExperiment={handleViewExperiments}
            onViewAnalytics={handleViewAnalytics}
            onOpenFeedback={handleOpenFeedback}
          />
        )}

        <PaginationBar
          currentPage={currentPage}
          totalPages={totalPages}
          totalCount={totalCount}
          pageSize={pageSize}
          onPrev={() => goToPage(currentPage - 1)}
          onNext={() => goToPage(currentPage + 1)}
          onPageClick={goToPage}
          onPageSizeChange={changePageSize}
        />

        {/* Empty State */}
        {templates.length === 0 && !loading && (
          <EmptyState
            searchQuery={searchQuery}
            categoryFilter={categoryFilter}
          />
        )}

        <DeleteConfirmDialog
          open={deleteDialogOpen}
          templateName={
            templateToDelete?.name || templateToDelete?.template_name
          }
          onClose={() => {
            if (!deleteLoading) {
              setDeleteDialogOpen(false);
              setTemplateToDelete(null);
            }
          }}
          onConfirm={() =>
            handleDeleteTemplate(
              templateToDelete?.id || templateToDelete?.template_id
            )
          }
          loading={deleteLoading}
        />

        <ExecutionHistoryDialog
          open={executionHistoryOpen}
          template={selectedTemplate}
          executions={executions}
          loading={executionsLoading}
          onClose={() => setExecutionHistoryOpen(false)}
        />

        <TwoStepExperimentDialog
          open={experimentDialogOpen}
          onClose={() => {
            setExperimentDialogOpen(false);
            setTemplateForExperiment(null);
          }}
          onSubmit={handleExperimentCreated}
          selectedTemplateId={
            templateForExperiment?.id || templateForExperiment?.template_id
          }
        />

        {selectedTemplateForAnalytics && (
          <TemplateAnalyticsDialog
            open={analyticsDialogOpen}
            onClose={() => {
              setAnalyticsDialogOpen(false);
              setSelectedTemplateForAnalytics(null);
            }}
            templateId={selectedTemplateForAnalytics.id}
            templateName={selectedTemplateForAnalytics.name}
          />
        )}

        {selectedTemplateForExperiments && (
          <TemplateExperimentsDialog
            open={experimentsDialogOpen}
            onClose={() => {
              setExperimentsDialogOpen(false);
              setSelectedTemplateForExperiments(null);
            }}
            templateId={selectedTemplateForExperiments.id}
            templateName={selectedTemplateForExperiments.name}
          />
        )}

        {selectedFeedbackTemplate && (
          <TemplateFeedbackDialog
            open={feedbackDialogOpen}
            template={selectedFeedbackTemplate}
            onClose={() => {
              setFeedbackDialogOpen(false);
              setSelectedFeedbackTemplate(null);
            }}
          />
        )}

        {/* Floating Action Button */}
        <Fab
          color="primary"
          aria-label="add template"
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
          }}
          onClick={() => navigate('/templates/new')}
        >
          <AddIcon />
        </Fab>
      </Box>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={MESSAGE_DURATION_SNACKBAR}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={closeSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default TemplateDashboard;
